import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, CreditCard } from "lucide-react";
import { supabase } from "@/lib/supabase";

export interface ECardLite {
  id: string;
  card_number: string | null;
  card_last4: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  email: string | null;
  taxicaller_account_id: string | null;
  status: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQuery?: string;
  /** When provided, hides eCards already linked to this CRM account. */
  excludeLinkedToAccountId?: string | null;
  onPick: (ecard: ECardLite) => void;
}

const ECardSearchDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  initialQuery = "",
  excludeLinkedToAccountId,
  onPick,
}) => {
  const [cards, setCards] = useState<ECardLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(initialQuery);

  useEffect(() => {
    if (!open) return;
    setQ(initialQuery || "");
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("ecards")
        .select(
          "id, card_number, card_last4, customer_first_name, customer_last_name, email, taxicaller_account_id, status"
        )
        .order("card_last4", { ascending: true })
        .limit(2000);
      if (cancelled) return;
      if (error) {
        console.error("eCard search load failed:", error);
        setCards([]);
      } else {
        setCards((data || []) as ECardLite[]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let result = cards;
    if (excludeLinkedToAccountId) {
      result = result.filter(
        (c) => c.taxicaller_account_id !== excludeLinkedToAccountId
      );
    }
    if (!s) return result.slice(0, 200);
    return result
      .filter((c) => {
        const fn = (c.customer_first_name || "").toLowerCase();
        const ln = (c.customer_last_name || "").toLowerCase();
        return (
          (c.card_last4 || "").toLowerCase().includes(s) ||
          (c.card_number || "").toLowerCase().includes(s) ||
          fn.includes(s) ||
          ln.includes(s) ||
          `${fn} ${ln}`.includes(s) ||
          (c.email || "").toLowerCase().includes(s) ||
          (c.taxicaller_account_id || "").toLowerCase().includes(s)
        );
      })
      .slice(0, 200);
  }, [cards, q, excludeLinkedToAccountId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Search eCard
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search last4, card #, name, email, TC account…"
              className="pl-8"
            />
          </div>

          <div className="border rounded-md max-h-[55vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading eCards…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                No matching eCards.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="text-left p-2">Last4</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">TC Account</th>
                    <th className="text-left p-2">Status</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const name =
                      [c.customer_first_name, c.customer_last_name]
                        .filter(Boolean)
                        .join(" ") || "—";
                    return (
                      <tr
                        key={c.id}
                        className="border-t border-border hover:bg-muted/30"
                      >
                        <td className="p-2 font-mono">
                          ****{c.card_last4 || "----"}
                        </td>
                        <td className="p-2">{name}</td>
                        <td className="p-2">{c.email || "—"}</td>
                        <td className="p-2 font-mono">
                          {c.taxicaller_account_id || "—"}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline">{c.status || "—"}</Badge>
                        </td>
                        <td className="p-2 text-right">
                          <Button
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => {
                              onPick(c);
                              onOpenChange(false);
                            }}
                          >
                            Select
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ECardSearchDialog;
